import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import ReceivingOrderLineItem from './ReceivingOrderLineItem';

import { Button, Text, Small, Panel, H3, H4, Table, TableFigure, Dropdown, Input, Box, Badge, Textarea } from '@bigcommerce/big-design';
import { CheckIcon, WarningIcon } from '@bigcommerce/big-design-icons';

import { findPurchaseOrder } from '../../redux/actions/purchaseOrders';
import { createWarehouseReceivingOrder } from '../../redux/actions/receivingOrders';

const NewReceivingOrderForm = ({ 
    payload,
    loadPurchaseOrder,
    purchaseOrders,
    newReceivingOrder,
    createWRO,
    settings,
}) => {
  let navigate = useNavigate();

  const [submitted, setSubmitted] = useState(false);
  const [scanTerm, setScanTerm] = useState('');

  const [scanBackgroundColor, setScanBackgroundColor] = useState('primary20');
  const [scanMessage, setScanMessage] = useState('Scan received items to receive below');

  const [notes, setNotes] = useState(null);

  const [receivedItems, setReceivedItems] = useState({});
  const [totalCount, setTotalCount] = useState(0.0);
  const { id } = useParams();

  useEffect(() => {
    loadPurchaseOrder(payload, id);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if(scanTerm !== ''){
        if(purchaseOrders.activePurchaseOrder) { receiveScan(scanTerm) };
        setScanTerm('');
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [scanTerm, 500]);

  useEffect(() => {
    if(newReceivingOrder) { navigate(-1) };
  }, [newReceivingOrder]);

  const updateScan = (event) => {
    let value = event.target.value;
    setScanTerm(value);
  }

  const setFieldValue = (setField, event) => {
    let value = event.target.value;
    setField(value);
  }

  const updateReceived = (event, purchaseOrderLineId) => {
    let value = event.target.value;
    let newItems = { ...receivedItems };
    let allItemsCount = 0;

    if(newItems[purchaseOrderLineId] === null || newItems[purchaseOrderLineId] === undefined) {
      newItems[purchaseOrderLineId] = {};
    }

    newItems[purchaseOrderLineId].count = parseFloat(value) || 0.0;

    for( var key in newItems ) {
      allItemsCount += newItems[key].count
    }

    setReceivedItems(newItems);
    setTotalCount(allItemsCount);
  }

  const updateTaxAdjustment = (event, purchaseOrderLineId) => {
    let value = event.target.value;
    let newItems = { ...receivedItems };
   
    if(newItems[purchaseOrderLineId] === null || newItems[purchaseOrderLineId] === undefined) {
      newItems[purchaseOrderLineId] = {};
    }

    if(!isNaN(parseFloat(value))) {
      newItems[purchaseOrderLineId].adjustedLineTax = parseFloat(value);
    } else {
      newItems[purchaseOrderLineId].adjustedLineTax = null;
    }

    setReceivedItems(newItems);
  }

  const receiveAll = (purchaseOrderLine) => {
    let newItems = { ...receivedItems };
    let allItemsCount = 0;

    if(newItems[purchaseOrderLine.id] === null || newItems[purchaseOrderLine.id] === undefined) {
      newItems[purchaseOrderLine.id] = {};
    }

    newItems[purchaseOrderLine.id].count = purchaseOrderLine.remaining_count || purchaseOrderLine.count;

    if(purchaseOrderLine.remaining_count === 0) {
      newItems[purchaseOrderLine.id].count = 0;
    }

    for( var key in newItems ) {
      allItemsCount += newItems[key].count
    }

    setReceivedItems(newItems);
    setTotalCount(allItemsCount);
  }

  const receiveOrder = () => {
    let newItems = { ...receivedItems };
    let allItemsCount = 0;

    purchaseOrders.activePurchaseOrder.purchase_order_lines.purchase_order_lines.forEach((purchaseOrderLine) => {
      if(newItems[purchaseOrderLine.id] === null || newItems[purchaseOrderLine.id] === undefined) {
        newItems[purchaseOrderLine.id] = {};
      }

      if(purchaseOrders.confirmedLineItems.indexOf(purchaseOrderLine.id) >= 0) {
        newItems[purchaseOrderLine.id].count = purchaseOrderLine.remaining_count || purchaseOrderLine.count;
      }
      
      if(purchaseOrderLine.remaining_count === 0) {
        newItems[purchaseOrderLine.id].count = 0;
      }
    });

    for( var key in newItems ) {
      allItemsCount += newItems[key].count
    }

    setReceivedItems(newItems);
    setTotalCount(allItemsCount);
  }

  const flashScanBar = () => {
    let scanBarElement = document.querySelector("#scan-bar");

    setTimeout(() => {
      scanBarElement?.classList?.add("blink-action");
    }, 500);

    setTimeout(() => {
      scanBarElement?.classList?.remove("blink-action");
    }, 1500);
  }

  const receiveScan = (scanTerm) => {
    let matchingLine = purchaseOrders.activePurchaseOrder.purchase_order_lines.purchase_order_lines.find(orderLine => (orderLine.source_sku === scanTerm) || (orderLine.source_upc === scanTerm))
  
    if(matchingLine) {
      let newItems = { ...receivedItems };

      if(newItems[matchingLine.id] === null || newItems[matchingLine.id] === undefined) {
        newItems[matchingLine.id] = { count: 0 };
      }

      newItems[matchingLine.id].count += 1;

      setReceivedItems(newItems);
      setTotalCount(totalCount + 1);

      setScanBackgroundColor('success20');
      setScanMessage(`Found ${scanTerm}, increased receive count by 1`);
    } else {
      setScanBackgroundColor('danger20');
      setScanMessage(`${scanTerm} not found.`);
    }
  }

  const submit = () => {
    setSubmitted(true);
    createWRO(payload, id, receivedItems, notes);
  }

  const description = () => {
    return(
      <> 
        <Text>Create a warehouse receiving order for this purchase order.</Text>
        <Text>Receive items from a shipment by updating receive counts in the "Purchase Order Line Items" panel. You can update this value in three ways: scan item barcodes as they're unpacked by clicking in the "Scan Barcode" bar and scanning item, barcodes, clicking the "Receive All" button on an item's row, or manually updating the "Receive Count" field directly.</Text>
        <Text>Scanning items will increase the count by 1 each time an item is scanned.</Text>
        <Text>The "Receive All" button will set the count to the total number ordered.</Text>
        <Text>Update taxes for lines in the "Update Tax Adjustment" field if new tax/duties information is available.</Text>
        <Text>Include additional notes (such as the shipment number being received) by filling in the Notes section below.</Text>
        <Text>When all the information about this warehouse receiving order has been input, click the "Submit Warehouse Receiving Order" button to complete the order.</Text>
        <Text>Each warehouse receiving order will appear as a new page in the purchase order PDF the next time it's downloaded.</Text>
        <Text>Purchase orders that have all of their items received will be marked fulfilled. When only some of their items have been received, purchase orders will be marked "partially fulfilled".</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#c66628' helpLink="bigcommerce/create-a-warehouse-receiving-order/" />
      <SettingsPage title="Update and Receive Order" description={description()}>
        <Panel>
          <H3>Update and Receive Order</H3>

          { purchaseOrders.activePurchaseOrder && 
            <>
              <Panel>
                <H4>Invoice: {purchaseOrders.activePurchaseOrder.invoice_number}</H4>
                <strong>Supplier Order: {purchaseOrders.activePurchaseOrder.supplier_order_number}</strong>
                <br/>
                <br/>
                
                <H4>Scan Barcodes</H4>

                <div className="flex-container">
                  <svg width="150" height="80" fill="#189dee" viewBox="0 0 32 32" id="svg5" version="1.1">
                    <defs id="defs2"/>
                    <g id="layer1" transform="translate(-108,-100)">
                      <path d="m 111,106 a 1.0001,1.0001 0 0 0 -1,1 v 3 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -2 h 2 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 z" id="path11698" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 134,106 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 2 v 2 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -3 a 1.0001,1.0001 0 0 0 -1,-1 z" id="path11700" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 137,121 a 1,1 0 0 0 -1,1 v 2 h -2 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 3 a 1.0001,1.0001 0 0 0 1,-1 v -3 a 1,1 0 0 0 -1,-1 z" id="path11702" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 111,121 a 1,1 0 0 0 -1,1 v 3 a 1.0001,1.0001 0 0 0 1,1 h 3 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 h -2 v -2 a 1,1 0 0 0 -1,-1 z" id="path11704" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 115,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11706" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 118,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11708" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 121,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11710" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 124,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11712" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 127,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11714" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 130,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11716" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                      <path d="m 133,110 a 1,1 0 0 0 -1,1 v 5.20703 1.31445 V 121 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 V 117.52148 116.20703 111 a 1,1 0 0 0 -1,-1 z" id="path11720" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                    </g>
                  </svg>
                  <div className="flex-4">
                    <Input
                      id="scan-bar"
                      required
                      label="Scan Barcode (SKU, UPC, EAN)"
                      onChange={updateScan}
                      placeholder="Click here to begin scanning"
                      type="text"
                      value={scanTerm}
                      disabled={submitted}
                    />
                    <br/>
                    <Box
                      backgroundColor={scanBackgroundColor}
                      border="box"
                      borderRadius="normal"
                      padding="medium"
                    >
                      {scanMessage}
                    </Box>
                  </div>
                </div>
                

              </Panel> 
            </>
          }

          { !purchaseOrders.activePurchaseOrder &&
            <LoadingSpinner statusText="Loading Purchase Order Details" />
          }
        </Panel>
        <Panel>
          { purchaseOrders.activePurchaseOrder &&
            <Button
              variant="subtle"
              onClick={() => receiveOrder()}
              iconLeft={<CheckIcon />}
            >
              Mark Entire Order Received
            </Button>
          }

          <H3>Purchase Order Line Items</H3>
          { !purchaseOrders.activePurchaseOrder &&
            <LoadingSpinner statusText="Loading Purchase Order Details" />
          }

          { purchaseOrders.activePurchaseOrder && purchaseOrders.activePurchaseOrder.purchase_order_lines.purchase_order_lines.map(purchaseOrderLine => 
              <ReceivingOrderLineItem 
                payload={payload}
                purchaseOrderLine={purchaseOrderLine}
                receivedItems={receivedItems}
                updateReceived={updateReceived}
                updateTaxAdjustment={updateTaxAdjustment}
                receiveAll={receiveAll}
                submitted={submitted}
              />
            )
          }
        </Panel>

        <Panel>
          <Textarea
            description="Receiving notes"
            label="Receiving Notes"
            onChange={(e) => setFieldValue(setNotes, e)}
            placeholder="Notes"
            resize={false}
            rows={3}
            value={notes}
          />
        </Panel>

        <Panel>
          <Button
            disabled={(totalCount === 0.0) || submitted}
            onClick={() => submit()}
            isLoading={submitted}
          >
            Submit Warehouse Receiving Order
          </Button>
        </Panel>
      </SettingsPage>
    </>
  )

}

const mapStateToProps = state => ({
  purchaseOrders: state.purchaseOrders,
  newReceivingOrder: state.receivingOrders.newReceivingOrder,
  settings: state.settings,
});

const mapDispatchToProps = (dispatch) => ({
  loadPurchaseOrder: (payload, purchaseOrderId) => dispatch(findPurchaseOrder(payload, purchaseOrderId, null)),
  createWRO: (payload, purchaseOrderId, receivedItems, notes) => dispatch(createWarehouseReceivingOrder(payload, purchaseOrderId, receivedItems, notes))
});

export default connect(mapStateToProps, mapDispatchToProps)(NewReceivingOrderForm);
